import { OrderStatus } from '@/coreDomainsModule/entities/arrival';
import { IBusiness } from '@/coreDomainsModule/entities/businessBase';
import { Order } from '@/coreDomainsModule/entities/order';
import { PartialDelivery } from '@/coreDomainsModule/entities/partialDelivery';
import { PartialDeliveryFactory } from '@/coreDomainsModule/factories/partialDeliveryFactory';

/**
 * 発注に関する操作
 */
export class OrderService {
  /**
   * 発注ステータスを変更する
   * @param order - 発注情報
   * @param changeOrderStatus - 変更先のステータス
   */
  static changeOrderStatus = (order: Order, changeOrderStatus: OrderStatus) => {
    // 変更元と変更先が同じ場合は何もしない
    if (order.orderStatus === changeOrderStatus) return;

    // 変更を反映する
    order.orderStatus = changeOrderStatus;

    // 変更先に応じた処理を行う
    switch (changeOrderStatus) {
      case OrderStatus.ApprovedAwaitingDeliveryDate:
        // 　分納が存在する場合は、削除する
        if (order.partialDeliveries.length > 0) {
          order.partialDeliveries.splice(0, order.partialDeliveries.length);
        }
        break;
      case OrderStatus.PartialDelivery:
        // 分納が存在しない場合は、分納を2つ追加する
        if (order.partialDeliveries.length === 0) {
          OrderService.addPartialDelivery(order);
          OrderService.addPartialDelivery(order);
        }
        break;
    }
  };

  /**
   * 分納の発注ステータスを変更する
   * @param order - 発注情報
   * @param partialDeliveryId - 分納ID
   * @param orderStatus - 変更先のステータス
   */
  static changePartialDeliveryStatus = (order: Order, partialDeliveryId: string, orderStatus: OrderStatus) => {
    const partialDelivery = order.partialDeliveries.find((pd) => pd.id === partialDeliveryId);
    // 変更元と変更先が同じ場合は何もしない
    if (!partialDelivery || partialDelivery.orderStatus === orderStatus) return;

    // 納入済みから別のステータスに変更された場合は、発注情報のステータスを変更する
    const isDelivered = partialDelivery.orderStatus === OrderStatus.Delivered;

    // 変更を反映する
    partialDelivery.orderStatus = orderStatus;

    if (isDelivered) {
      // 納入済みから別のステータスに変更された場合は、発注情報のステータスを変更する
      OrderService.changeOrderStatus(order, OrderStatus.PartialDelivery);
    }
  };

  /**
   * 発注が指定の発注ステータスに遷移可能かどうかを判定する
   * @param order - 発注情報
   * @param orderStatus - 遷移先の発注ステータス
   */
  static canTransitionOrderStatus = (order: Order, orderStatus: OrderStatus): boolean => {
    switch (order.orderStatus) {
      case OrderStatus.PartialDelivery:
        // 分納の場合は、全ての分納の発注ステータスが納入済みになっている場合のみ、納入済みに遷移可能
        switch (orderStatus) {
          case OrderStatus.ApprovedAwaitingDeliveryDate:
            return true;
          case OrderStatus.Delivered:
            return order.partialDeliveries.every((pd) => pd.orderStatus === OrderStatus.Delivered);
          default:
            return false;
        }
      case OrderStatus.Delivered:
        // 分納がある場合は、分納のみに遷移可能
        if (order.partialDeliveries.length > 0) {
          return orderStatus === OrderStatus.PartialDelivery;
        }
      default:
        switch (orderStatus) {
          case OrderStatus.Recommended:
          case OrderStatus.ApprovalRequested:
            return false;
          default:
            return true;
        }
    }
  };

  /**
   * 数量が変更可能かどうかを判定する
   * @param order - 発注情報
   */
  static canChangeOrderAmount = (order: Order): boolean => {
    // 納期回答待ち、確定、納入済みの場合は数量変更不可
    switch (order.orderStatus) {
      case OrderStatus.QuantityConfirmed:
      case OrderStatus.Confirmed:
      case OrderStatus.Delivered:
        return false;
      default:
        return true;
    }
  };

  /**
   * 納期が変更可能かどうかを判定する
   * @param order - 発注情報
   */
  static canChangeOrderDeliveryDate = (order: Order): boolean => {
    // 納期回答待ち、確定、納入済みの場合は納期変更不可
    switch (order.orderStatus) {
      case OrderStatus.DeliveryDateConfirmed:
      case OrderStatus.Confirmed:
      case OrderStatus.Delivered:
        return false;
      default:
        return true;
    }
  };

  /**
   * 発注情報の整合性を確認する
   * @param order - 発注情報
   */
  static checkOrderIntegrity = (order: Order): boolean => {
    switch (order.orderStatus) {
      case OrderStatus.PartialDelivery:
        // 分納の発注数量の合計値が発注の発注数量の値と等しいかどうか確認する
        const totalOrderAmount = order.partialDeliveries.reduce((acc, pd) => acc + pd.orderAmount, 0);
        return totalOrderAmount === order.orderAmount;
      default:
        return true;
    }
  };

  /**
   * 分納を追加する
   * @param order - 発注情報
   */
  static addPartialDelivery = (order: Order) => {
    const newPartialDelivery: PartialDelivery = PartialDeliveryFactory.create({
      orderId: order.orderId,
      aggregateBusinessId: order.aggregateBusinessId,
      deliveryDate: order.estimatedTimeOfArrival,
    });
    order.partialDeliveries.push(newPartialDelivery);
  };

  /**
   * 分納を削除する
   * @param order - 発注情報
   * @param partialDelivery - 分納情報
   */
  static deletePartialDelivery(order: Order, partialDelivery: PartialDelivery): void {
    // 対象の分納のインデックスを取得する
    const index = order.partialDeliveries.findIndex((pd) => pd.id === partialDelivery.id);
    if (index !== -1) {
      // splice()を使用してリアクティブな配列更新を行う
      order.partialDeliveries.splice(index, 1);
    }

    // 削除後、残る分納が1件の場合は分納情報を解除し、発注情報の状態を更新する
    if (order.partialDeliveries.length === 1) {
      order.orderStatus = OrderStatus.ApprovedAwaitingDeliveryDate;
      order.desiredDeliveryDate = order.estimatedTimeOfArrival;
      // 配列を再代入するのではなく、中身をsplice()でクリアする
      order.partialDeliveries.splice(0, order.partialDeliveries.length);
    }

    // 削除後、deliveryDateがreactiveに更新されないため、+1-1の操作を入れておくことおで、再描画を行う
    for (let i = 0; i < order.partialDeliveries.length; i++) {
      order.partialDeliveries[i].deliveryDate++;
      order.partialDeliveries[i].deliveryDate--;
    }
  }
}
